import React, { useState, useRef, useEffect, useContext } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import Header from './Header.jsx'
import "../css/CreatePlot.css"
import { domain } from './Domain.jsx'
import useApiRequest from './useApiRequest.jsx'
import useFetch from './useFetch.jsx'
import { useLoadingContext } from './LoadingContext.jsx'
import notify from './Nots.jsx'
import { saveToLocalStorage, getFromLocalStorage } from './EncryptDecrypt.jsx'
import { useEventContext } from './EventContext.jsx'
import EventPreview from './EventPreview.jsx'
import { useUrlContext } from './RouterLogger.jsx'
import { Modal, Button, Form } from "react-bootstrap";
import { motion, AnimatePresence } from 'framer-motion'
import MyMapComponent from './MapComponent.jsx'
import LocationPicker from './MapLocationPicker.jsx'
import { LocationContext } from './LocationContext.jsx'
import { useLoginContext } from './LoginContext.jsx'
import { 
  User, Calendar, Ticket, Eye, ArrowRight, ArrowLeft, 
  CheckCircle, Circle, MapPin, Clock, Globe, 
  Facebook, Twitter, Instagram, Link as LinkIcon,
  Upload, Image, DollarSign, Users, Settings,
  Plus, Edit, Save, Trash2, AlertCircle, Phone
} from 'lucide-react'

const Nav = ({ selected, setSelected }) => {
  const steps = [
    { id: 0, title: "Organiser Profile", icon: <User className="w-4 h-4" /> },
    { id: 1, title: "Event Details", icon: <Calendar className="w-4 h-4" /> },
    { id: 2, title: "Ticket Information", icon: <Ticket className="w-4 h-4" /> },
    { id: 3, title: "Event Preview", icon: <Eye className="w-4 h-4" /> }
  ];

  return (
    <div className="bg-white shadow-lg border-b">
      <div className="w-full md:w-[70%] mx-auto px-4 py-6">
        <div className="flex flex-col md:flex-row items-center justify-between gap-4">
          <h1 className="text-lg!  font-bold text-gray-800">Create Your Event</h1>
          <div className="flex items-center gap-2 md:gap-4">
            {steps.map((step, index) => (
              <div key={step.id} className="flex items-center">
                <motion.button
                  onClick={() => setSelected(step.id)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all duration-300 ${
                    selected === step.id
                      ? 'bg-gradient-to-r from-orange-400 to-orange-500 text-white shadow-lg'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  {selected > step.id ? (
                    <CheckCircle className="w-4 h-4" />
                  ) : (
                    step.icon
                  )}
                  <span className="hidden sm:inline">{step.title}</span>
                  <span className="sm:hidden">{step.id + 1}</span>
                </motion.button>
                {index < steps.length - 1 && (
                  <ArrowRight className="w-4 h-4 text-gray-400 mx-2" />
                )}
              </div>
            ))}
          </div>
        </div>
        
        {/* Progress Bar */}
        <div className="mt-4">
          <div className="w-full bg-gray-200 rounded-full h-2">
            <motion.div
              className="bg-gradient-to-r from-orange-400 to-orange-500 h-2 rounded-full"
              initial={{ width: 0 }}
              animate={{ width: `${((selected + 1) / steps.length) * 100}%` }}
              transition={{ duration: 0.5 }}
            />
          </div>
          <p className="text-sm text-gray-600 mt-2 text-center">
            Step {selected + 1} of {steps.length}
          </p>
        </div>
      </div>
    </div>
  )
}

const OrganiserProfile = ({ setSelected, setLoading, event, setEvent }) => {
  const { getRequest, postRequest, putRequest } = useApiRequest()
  const initialUserData = {
    contact: "",
    email: "",
    organiser_name: "",
    facebook: "",
    twitter: "",
    website: ""
  };
  const [userData, setUserData] = useState(initialUserData)
  const [reload, setReload] = useState(false)
  const [errors, setErrors] = useState({})

  // Function to reset all userData values to empty strings
  const resetUserData = () => {
    setUserData(initialUserData);
    setErrors({});
  };

  // Function to handle changes in form inputs
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setUserData((prevUserData) => ({
      ...prevUserData,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  // Validation function
  const validateForm = () => {
    const newErrors = {};
    
    if (!userData.contact.trim()) {
      newErrors.contact = 'Contact number is required';
    } else if (!/^\+?[\d\s\-\(\)]+$/.test(userData.contact)) {
      newErrors.contact = 'Please enter a valid contact number';
    }
    
    if (!userData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(userData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    
    if (!userData.organiser_name.trim()) {
      newErrors.organiser_name = 'Organiser name is required';
    }
    
    if (userData.facebook && !/^https?:\/\/.+/.test(userData.facebook)) {
      newErrors.facebook = 'Please enter a valid URL starting with http:// or https://';
    }
    
    if (userData.twitter && !/^https?:\/\/.+/.test(userData.twitter)) {
      newErrors.twitter = 'Please enter a valid URL starting with http:// or https://';
    }
    
    if (userData.website && !/^https?:\/\/.+/.test(userData.website)) {
      newErrors.website = 'Please enter a valid URL starting with http:// or https://';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  //oncomponent load, check local storage for presence of key
  useEffect(() => {
    const eventReg = getFromLocalStorage("eventReg")
    const fetchUserData = async () => {
      const response = await getRequest(`${domain}/organiser-profiles?slug=${eventReg}`);
      console.log(response)
      if (response.status === 200) {
        setUserData((prevUserData) => ({
          ...prevUserData,
          id: response.body.id,
          event: response.body.id,
          eventName: response.body.event_plot_name,
          contact: response.body.contact,
          email: response.body.email,
          organiser_name: response.body.organiser_name,
          facebook: response.body.facebook,
          twitter: response.body.twitter,
          website: response.body.website
        }));
      }
    };
    if (eventReg !== null) {
      fetchUserData()
    }
  }, [reload])

  const handleProfile = async (event) => {
    event.preventDefault();
    
    if (!validateForm()) {
      notify('Please fix the errors in the form', 'error');
      return;
    }
    
    setLoading(true)
    const formData = new FormData(event.target)
    let response;
    if (userData.id) {
      response = await putRequest(`${domain}/organiser-profiles/${userData.id}`, formData, true)
    } else {
      response = await postRequest(`${domain}/organiser-profiles`, formData, true)
    }

    if (response.status === 201) {
      //save to local storage for useage at a later point if page is refreshed!
      saveToLocalStorage("eventReg", response.body.data.slug);
      //save event data to context
      setEvent((prevEvent) => ({
        ...prevEvent,
        event: response.body.data.id,
        eventName: response.body.data.event_plot_name
      }));
      notify('Profile saved successfully!', "success")
      setSelected(1); //move to the next page
    } else {
      notify(response.body.data || 'Failed to save profile', "error")
    }
    event.target.reset()
    resetUserData()
    setLoading(false)
    setReload(!reload)
  };

  const [checked, setChecked] = useState(false);

  const handleCheckboxChange = (e) => {
    setChecked(e.target.checked);
  };

  return (
    <motion.div 
      className='min-h-screen bg-gray-50 py-8'
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <div className="w-full md:w-[70%] mx-auto px-4 py-6">
        <div className="bg-white rounded-xl shadow-lg p-8">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 bg-gradient-to-br from-orange-400 to-orange-500 rounded-xl flex items-center justify-center">
              <User className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-800">Organiser Profile</h2>
              <p className="text-gray-600">Tell us about yourself and your organization</p>
            </div>
          </div>

          <form onSubmit={handleProfile} className="space-y-6">
            {/* Contact Information */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                <Phone className="w-5 h-5 text-orange-400" />
                Contact Information
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Contact Number *
                  </label>
                  <input 
                    type="tel" 
                    name="contact" 
                    required 
                    value={userData.contact} 
                    onChange={handleInputChange} 
                    placeholder="+256 700 123 456" 
                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-orange-400 focus:border-transparent transition-all duration-200 ${
                      errors.contact ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  {errors.contact && (
                    <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
                      <AlertCircle className="w-4 h-4" />
                      {errors.contact}
                    </p>
                  )}
        </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address *
                  </label>
                  <input 
                    type="email" 
                    name="email" 
                    required 
                    value={userData.email} 
                    onChange={handleInputChange} 
                    placeholder="your@email.com" 
                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-orange-400 focus:border-transparent transition-all duration-200 ${
                      errors.email ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  {errors.email && (
                    <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
                      <AlertCircle className="w-4 h-4" />
                      {errors.email}
                    </p>
                  )}
          </div>
        </div>
              <p className="text-sm text-gray-600">
                These contacts will be added to the ticket for any inquiries regarding this event.
              </p>
        </div>

            {/* Organiser Name */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                <User className="w-5 h-5 text-orange-400" />
                Organiser Name
              </h3>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Organization Name *
                </label>
                <input 
                  type="text" 
                  name="organiser_name" 
                  required 
                  value={userData.organiser_name} 
                  onChange={handleInputChange} 
                  placeholder="Your Organization Name" 
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-orange-400 focus:border-transparent transition-all duration-200 ${
                    errors.organiser_name ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {errors.organiser_name && (
                  <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
                    <AlertCircle className="w-4 h-4" />
                    {errors.organiser_name}
                  </p>
                )}
              </div>
              <p className="text-sm text-gray-600">
                This will be the organiser name displayed on your event.
              </p>
            </div>

            {/* Social Media Section */}
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <input 
                  className="w-5 h-5 text-orange-400 border-gray-300 rounded focus:ring-orange-400" 
                  checked={checked} 
                  value={checked} 
                  onChange={handleCheckboxChange} 
                  name="online_event" 
                  type="checkbox" 
                />
                <label className="text-sm font-medium text-gray-700">
                  Include social media URLs
                </label>
              </div>
              
        <AnimatePresence>
                {checked && (
            <motion.div
                    className="space-y-4 p-6 bg-gray-50 rounded-lg border border-gray-200"
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <h4 className="text-md font-semibold text-gray-800 flex items-center gap-2">
                      <Globe className="w-4 h-4 text-orange-400" />
                      Social Media URLs
                    </h4>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          <Facebook className="w-4 h-4 inline mr-2 text-blue-600" />
                          Facebook URL
                        </label>
                <input
                  type="url"
                  name="facebook"
                  value={userData.facebook}
                  onChange={handleInputChange}
                          placeholder="https://facebook.com/your-page"
                          className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-orange-400 focus:border-transparent transition-all duration-200 ${
                            errors.facebook ? 'border-red-500' : 'border-gray-300'
                          }`}
                        />
                        {errors.facebook && (
                          <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
                            <AlertCircle className="w-4 h-4" />
                            {errors.facebook}
                          </p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          <Twitter className="w-4 h-4 inline mr-2 text-sky-500" />
                          X (Twitter) URL
                        </label>
                <input
                  type="url"
                  name="twitter"
                  value={userData.twitter}
                  onChange={handleInputChange}
                          placeholder="https://x.com/your-handle"
                          className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-orange-400 focus:border-transparent transition-all duration-200 ${
                            errors.twitter ? 'border-red-500' : 'border-gray-300'
                          }`}
                        />
                        {errors.twitter && (
                          <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
                            <AlertCircle className="w-4 h-4" />
                            {errors.twitter}
                          </p>
                        )}
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        <LinkIcon className="w-4 h-4 inline mr-2 text-gray-600" />
                        Website URL
                      </label>
                <input
                  type="url"
                  name="website"
                  value={userData.website}
                  onChange={handleInputChange}
                        placeholder="https://yourwebsite.com"
                        className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-orange-400 focus:border-transparent transition-all duration-200 ${
                          errors.website ? 'border-red-500' : 'border-gray-300'
                        }`}
                      />
                      {errors.website && (
                        <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
                          <AlertCircle className="w-4 h-4" />
                          {errors.website}
                        </p>
                      )}
                    </div>
              </motion.div>
                )}
        </AnimatePresence>
        </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 pt-6 border-t border-gray-200">
              {userData.id ? (
                <motion.button 
                  type='submit' 
                  className="flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-orange-400 to-orange-500 text-white rounded-lg font-medium hover:from-orange-500 hover:to-orange-600 transition-all duration-200 shadow-lg hover:shadow-xl"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <Edit className="w-4 h-4" />
                  Update Profile
                </motion.button>
              ) : (
                <motion.button 
                  type='submit' 
                  className="flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-orange-400 to-orange-500 text-white rounded-lg font-medium hover:from-orange-500 hover:to-orange-600 transition-all duration-200 shadow-lg hover:shadow-xl"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <Save className="w-4 h-4" />
                  Save Profile
                </motion.button>
              )}
              
              <motion.button 
                type="button" 
                onClick={resetUserData} 
                className="flex items-center justify-center gap-2 px-6 py-3 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200 transition-all duration-200"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <Trash2 className="w-4 h-4" />
                Clear Form
              </motion.button>
            </div>
      </form>
    </div>
      </div>
    </motion.div>
  )
}

const EventDetail = ({ setSelected, setLoading }) => {
  const { markerPosition } = useContext(LocationContext); // Access the context
  const { postRequest, getRequest, putRequest } = useApiRequest()
  const { data, loading, refresh, setRefresh } = useFetch(`${domain}/event-categories`)
  const [errors, setErrors] = useState({})
  const initialUserData = {
    event_plot_name: "",
    organiser_slug: "",
    event_category: "",
    event_description: "",
    allow_event_reviews: false,
    restriction: "",
    banner: "",
    listing_img: "",
    start_date_event: "",
    end_date_event: "",
    venue_name: "",
    online_event: false,
    online_link: "",
    venue_address: "",
    refund_policy: "",
    gps_cordinates: false,
    latitude: "",
    longitude: "",
    category: "",
    event_capacity: "",
  };
  const banner = useRef()
  const bannerImg = useRef()
  const listing = useRef()
  const listingRef = useRef()
  const [error, setError] = useState(null);
  const [reload, setReload] = useState(false)

  const [bannerSrc, setBannerSrc] = useState({
    banner: "/images/download.png",
    listing: "/images/download.png"
  })
  const [eventData, setEventData] = useState(initialUserData)

  const AGE_RESTRICTIONS = [
    { value: '18+', label: '18+ (For mature content or legal reasons)' },
    { value: '21+', label: '21+ (For events involving alcohol)' },
    { value: '13+', label: '13+ (Suitable for teenagers)' },
    { value: 'Family Friendly', label: 'Family Friendly (Open to all ages)' },
    { value: '5-12', label: '5-12 Years (For children in this age range)' },
  ];

  const getLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          setEventData((prevEventData) => ({
            ...prevEventData,
            latitude: latitude,
            longitude: longitude
          }));
        },
        (err) => {
          setError(err.message);
        }
      );
    } else {
      setError("Geolocation is not supported by this browser.");
    }
  };

  /*
  useEffect(() => {
    eventData.gps_cordinates && getLocation()
  }, [eventData.gps_cordinates])
  */

  // Sync markerPosition with eventData.latitude and eventData.longitude
  /*
  useEffect(() => {
    console.log("This is the new marker position!");
    console.log(markerPosition)
    if (markerPosition) {
      setEventData((prev) => ({
        ...prev,
        latitude: markerPosition.lat,
        longitude: markerPosition.lng,
      }));
    }
  }, [markerPosition]);
  */

  useEffect(() => {
    if (markerPosition) {
      setEventData((prev) => {
        // Only update if values have changed
        if (prev.latitude !== markerPosition.lat || prev.longitude !== markerPosition.lng) {
          return {
            ...prev,
            latitude: markerPosition.lat,
            longitude: markerPosition.lng,
          };
        } else {
          return prev; // no state change if coordinates are the same
        }
      });
    }
  }, [markerPosition]);

  //oncomponent load, check local storage for presence of key and load event details captured initially in the online system / db
  useEffect(() => {
    const eventReg = getFromLocalStorage("eventReg")
    const fetchEventData = async () => {
      const response = await getRequest(`${domain}/event-details?slug=${eventReg}`);
      if (response.status === 200 && response.body.id) {
        setEventData((prevUserData) => ({
          ...prevUserData,
          id: response.body.id,
          event: response.body.id,
          eventName: response.body.event_plot_name,//added these below to populate the information needed
          event_plot_name: response.body.event_plot_name,
          organiser_slug: response.body.organiser_slug,
          event_category: response.body.event_category?.id || response.body.event_category,
          event_description: response.body.event_description,
          allow_event_reviews: response.body.allow_event_reviews,
          restriction: response.body.restriction,
          start_date_event: response.body.start_date_event,
          end_date_event: response.body.end_date_event,
          venue_name: response.body.venue_name,
          online_event: response.body.online_event,
          online_link: response.body.online_link,
          venue_address: response.body.venue_address,
          refund_policy: response.body.refund_policy,
          gps_cordinates: response.body.gps_cordinates,
          latitude: response.body.latitude,
          longitude: response.body.longitude,
          category: response.body.category,
          event_capacity: response.body.event_capacity
        }));

        // Update the preview state
        setBannerSrc((prevEventData) => ({
          ...prevEventData,
          banner: domain + response.body.banner,
          listing: domain + response.body.listing_img
        }));
      }
      else {
        setEventData((prevEventData) => ({
          ...prevEventData,
          organiser_slug: eventReg
        }));
      }
    };
    fetchEventData()
  }, [reload])

  const handleImageChange = (e) => {
    const file = e.target.files[0];

    if (file) {
      // Update the preview state
      setBannerSrc((prevEventData) => ({
        ...prevEventData,
        banner: URL.createObjectURL(file)
      }));

      // Update the actual file data state
      setEventData((prevEventData) => ({
        ...prevEventData,
        banner: file
      }));
    }
  }

  const handleImageChange2 = (e) => {
    const file = e.target.files[0];

    if (file) {
      // Update the preview state
      setBannerSrc((prevEventData) => ({
        ...prevEventData,
        listing: URL.createObjectURL(file)
      }));

      // Update the actual file data state
      setEventData((prevEventData) => ({
        ...prevEventData,
        listing_img: file
      }));
    }
  }

  // Function to reset all userData values to empty strings
  const resetEventData = () => {
    setEventData(initialUserData);
    setErrors({});
  };

  // Function to handle changes in form inputs
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (type === 'checkbox') {
      setEventData((prevEventData) => ({
        ...prevEventData,
        [name]: checked
      }));
    } else {
      setEventData((prevEventData) => ({
        ...prevEventData,
        [name]: value
      }));
    }
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  // Validation function
  const validateForm = () => {
    const newErrors = {};
    
    if (!eventData.event_plot_name.trim()) {
      newErrors.event_plot_name = 'Event name is required';
    }
    
    if (!eventData.event_category) {
      newErrors.event_category = 'Please select an event category';
    }
    
    if (!eventData.event_description.trim()) {
      newErrors.event_description = 'Event description is required';
    }
    
    if (!eventData.start_date_event) {
      newErrors.start_date_event = 'Start date is required';
    }
    
    if (!eventData.end_date_event) {
      newErrors.end_date_event = 'End date is required';
    }
    
    if (new Date(eventData.end_date_event) < new Date(eventData.start_date_event)) {
      newErrors.end_date_event = 'End date must be on or after start date';
    }
    
    if (!eventData.venue_name.trim()) {
      newErrors.venue_name = 'Venue name is required';
    }
    
    if (!eventData.venue_address.trim()) {
      newErrors.venue_address = 'Venue address is required';
    }
    
    if (eventData.online_event && !eventData.online_link.trim()) {
      newErrors.online_link = 'Online link is required for online events';
    }
    
    if (eventData.online_link && !/^https?:\/\/.+/.test(eventData.online_link)) {
      newErrors.online_link = 'Please enter a valid URL starting with http:// or https://';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleEventDetails = async (event) => {
    event.preventDefault()
    
    if (!validateForm()) {
      notify('Please fix the errors in the form', 'error');
      return;
    }
    
    setLoading(true)
    const formData = new FormData()
    // Loop through each property in the initialUserData object
    for (const [key, value] of Object.entries(eventData)) {
      // Append each key-value pair to the FormData object
      if (eventData.id && key === 'event_category') {
        // Convert string ID to integer for backend
        formData.append(key, parseInt(value));
      } else if (key === 'latitude' || key === 'longitude') {
        // Append latitude and longitude only if the value is not empty
        if (value !== "") {
          formData.append(key, value);
        } else {
          console.log(`Skipping empty ${key} value!`);
        }
      } else {
        formData.append(key, value);
      }
    }
    let response;
    if (eventData.id) {
      response = await putRequest(`${domain}/event-details/${getFromLocalStorage("eventReg")}`, formData, true)
    } else {
      response = await postRequest(`${domain}/event-details`, formData, true)
    }

    if (response.status === 201 || response.status === 202) {
      notify('Event details saved successfully!', "success")
      setSelected(2) //move to the next page
    } else {
      notify(response.body.data || 'Failed to save event details', "error")
    }
    event.target.reset()
    resetEventData()
    setLoading(false)
    setReload(!reload)
    //reset the banner src
    setBannerSrc((prevEventData) => ({
      ...prevEventData,
      banner: "/images/download.png",
      listing: "/images/download.png"
    }));
  };

  return (
    <motion.div 
      className='min-h-screen bg-gray-50 py-8'
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <div className="w-full md:w-[70%] mx-auto px-4 py-6">
        <div className="bg-white rounded-xl shadow-lg p-8">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 bg-gradient-to-br from-orange-400 to-orange-500 rounded-xl flex items-center justify-center">
              <Calendar className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-800">Event Details</h2>
              <p className="text-gray-600">Tell us about your event</p>
            </div>
          </div>

          <form encType="multipart/form-data" onSubmit={handleEventDetails} className="space-y-8">
            {/* Basic Event Information */}
            <div className="space-y-6">
              <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                <Calendar className="w-5 h-5 text-orange-400" />
                Basic Information
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Event Category *
                  </label>
                  <select 
                    name="event_category" 
                    value={eventData.event_category} 
                    onChange={handleInputChange} 
                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-orange-400 focus:border-transparent transition-all duration-200 ${
                      errors.event_category ? 'border-red-500' : 'border-gray-300'
                    }`}
                  >
                    <option value="">Select the event category</option>
              {data && data.map(item =>
                <option key={item.id} value={item.id}>{item.name}</option>
              )}
            </select>
                  {errors.event_category && (
                    <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
                      <AlertCircle className="w-4 h-4" />
                      {errors.event_category}
                    </p>
                  )}
                  <p className="text-sm text-gray-600 mt-1">Select category for this event.</p>
          </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Event Name *
                  </label>
                  <input 
                    type="text" 
                    name="event_plot_name" 
                    value={eventData.event_plot_name} 
                    onChange={handleInputChange} 
                    placeholder="Enter the event name here" 
                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-orange-400 focus:border-transparent transition-all duration-200 ${
                      errors.event_plot_name ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  {errors.event_plot_name && (
                    <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
                      <AlertCircle className="w-4 h-4" />
                      {errors.event_plot_name}
                    </p>
                  )}
                  <p className="text-sm text-gray-600 mt-1">Event name displayed to the public</p>
          </div>
        </div>

              {/* Event Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Event Description *
                </label>
                <textarea 
                  name="event_description" 
                  rows="5" 
                  value={eventData.event_description} 
                  onChange={handleInputChange} 
                  placeholder="Enter the event description here" 
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-orange-400 focus:border-transparent transition-all duration-200 ${
                    errors.event_description ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {errors.event_description && (
                  <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
                    <AlertCircle className="w-4 h-4" />
                    {errors.event_description}
                  </p>
                )}
                <p className="text-sm text-gray-600 mt-1">Provide all the description available to the event, this eases clients work in selection of an event to attend.</p>
        </div>

              {/* Event Settings */}
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <input 
                    className="w-5 h-5 text-orange-400 border-gray-300 rounded focus:ring-orange-400" 
                    checked={eventData.allow_event_reviews} 
                    onChange={handleInputChange} 
                    type="checkbox" 
                    name="allow_event_reviews" 
                  />
                  <label className="text-sm font-medium text-gray-700">
                    Allow Event Reviews
                  </label>
        </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Age Restriction
                  </label>
                  <select 
                    name="restriction" 
                    value={eventData.restriction} 
                    onChange={handleInputChange} 
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-400 focus:border-transparent transition-all duration-200"
                  >
                    <option value="">Select a suitable restriction</option>
            {AGE_RESTRICTIONS.map(item =>
              <option key={item.value} value={item.value}>{item.label}</option>
            )}
          </select>
                  <p className="text-sm text-gray-600 mt-1">The age restriction specifies which category of people can attend the created event.</p>
        </div>
            </div>
            </div>

            {/* Event Images */}
            <div className="space-y-6">
              <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                <Image className="w-5 h-5 text-orange-400" />
                Event Images
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <input 
                    type="file" 
                    ref={banner} 
                    name="banner" 
                    onChange={handleImageChange} 
                    accept="image/*" 
                    className="hidden" 
                  />
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Cover Image
                  </label>
                  <div 
                    onClick={() => banner.current.click()}
                    className="w-full h-48 border-2 border-dashed border-gray-300 rounded-lg flex flex-col items-center justify-center cursor-pointer hover:border-orange-400 transition-colors duration-200"
                  >
                    <img 
                      src={bannerSrc.banner} 
                      alt="Cover preview" 
                      className="w-full h-full object-cover rounded-lg"
                    />
                  </div>
                  <p className="text-sm text-gray-600 mt-1">This will be the image displayed on our details page for the events.</p>
          </div>

                <div>
                  <input 
                    type="file" 
                    ref={listing} 
                    name="listing_img" 
                    onChange={handleImageChange2} 
                    accept="image/*" 
                    className="hidden" 
                  />
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Event Listing Image
                  </label>
                  <div 
                    onClick={() => listing.current.click()}
                    className="w-full h-48 border-2 border-dashed border-gray-300 rounded-lg flex flex-col items-center justify-center cursor-pointer hover:border-orange-400 transition-colors duration-200"
                  >
                    <img 
                      src={bannerSrc.listing} 
                      alt="Listing preview" 
                      className="w-full h-full object-cover rounded-lg"
                    />
        </div>
                  <p className="text-sm text-gray-600 mt-1">This will be the image displayed on our events page for the event previews.</p>
          </div>
          </div>
        </div>

            {/* Event Schedule */}
            <div className="space-y-6">
              <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                <Clock className="w-5 h-5 text-orange-400" />
                Event Schedule
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Start Date *
                  </label>
                  <input 
                    type="date" 
                    name="start_date_event" 
                    value={eventData.start_date_event} 
                    onChange={handleInputChange} 
                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-orange-400 focus:border-transparent transition-all duration-200 ${
                      errors.start_date_event ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  {errors.start_date_event && (
                    <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
                      <AlertCircle className="w-4 h-4" />
                      {errors.start_date_event}
                    </p>
                  )}
                  <p className="text-sm text-gray-600 mt-1">Enter the event start date.</p>
        </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    End Date *
                  </label>
                  <input 
                    type="date" 
                    name="end_date_event" 
                    value={eventData.end_date_event} 
                    onChange={handleInputChange} 
                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-orange-400 focus:border-transparent transition-all duration-200 ${
                      errors.end_date_event ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  {errors.end_date_event && (
                    <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
                      <AlertCircle className="w-4 h-4" />
                      {errors.end_date_event}
                    </p>
                  )}
                  <p className="text-sm text-gray-600 mt-1">Enter the event end date.</p>
        </div>
        </div>
            </div>

            {/* Venue Information */}
            <div className="space-y-6">
              <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                <MapPin className="w-5 h-5 text-orange-400" />
                Venue Information
              </h3>
              
              <div className="space-y-4">

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Venue Address *
                  </label>
                  <textarea 
                    name="venue_address" 
                    value={eventData.venue_address} 
                    onChange={handleInputChange} 
                    rows="3" 
                    placeholder="E.g. Sheraton Hotel Kampala - Uganda" 
                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-orange-400 focus:border-transparent transition-all duration-200 ${
                      errors.venue_address ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  {errors.venue_address && (
                    <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
                      <AlertCircle className="w-4 h-4" />
                      {errors.venue_address}
                    </p>
                  )}
                  <p className="text-sm text-gray-600 mt-1">Please specify the event address clearly e.g. Sheraton Hotel Kampala - Uganda.</p>
          </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Venue Name *
                  </label>
                  <input 
                    type="text" 
                    name="venue_name" 
                    value={eventData.venue_name} 
                    onChange={handleInputChange} 
                    placeholder="e.g. Rwenzori Conference Hall" 
                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-orange-400 focus:border-transparent transition-all duration-200 ${
                      errors.venue_name ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  {errors.venue_name && (
                    <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
                      <AlertCircle className="w-4 h-4" />
                      {errors.venue_name}
                    </p>
                  )}
                  <p className="text-sm text-gray-600 mt-1">Please specify the event venue name e.g Rwenzori Conference Hall.</p>
        </div>


                <div className="flex items-center gap-3">
                  <input 
                    className="w-5 h-5 text-orange-400 border-gray-300 rounded focus:ring-orange-400" 
                    checked={eventData.online_event} 
                    onChange={handleInputChange} 
                    name="online_event" 
                    type="checkbox" 
                  />
                  <label className="text-sm font-medium text-gray-700">
                    Is this an online event?
                  </label>
        </div>

                <AnimatePresence>
                  {eventData.online_event && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.3 }}
                      className="space-y-4 p-4 bg-gray-50 rounded-lg border border-gray-200"
                    >
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Online Link URL *
                      </label>
                      <input 
                        type="url" 
                        name="online_link" 
                        value={eventData.online_link} 
                        onChange={handleInputChange} 
                        placeholder="https://meet.google.com/..." 
                        className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-orange-400 focus:border-transparent transition-all duration-200 ${
                          errors.online_link ? 'border-red-500' : 'border-gray-300'
                        }`}
                      />
                      {errors.online_link && (
                        <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
                          <AlertCircle className="w-4 h-4" />
                          {errors.online_link}
                        </p>
                      )}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
              </div>

            {/* Additional Information */}
            <div className="space-y-6">
              <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                <Settings className="w-5 h-5 text-orange-400" />
                Additional Information
              </h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Refund Policy
                  </label>
                  <textarea 
                    name="refund_policy" 
                    value={eventData.refund_policy} 
                    onChange={handleInputChange} 
                    rows="3" 
                    placeholder="Provide all the available refund policy to the ticket." 
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-400 focus:border-transparent transition-all duration-200"
                  />
                  <p className="text-sm text-gray-600 mt-1">Provide all the available refund policy to the ticket.</p>
            </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Event Capacity
                  </label>
                  <input 
                    type="number" 
                    name="event_capacity" 
                    value={eventData.event_capacity} 
                    onChange={handleInputChange} 
                    placeholder="Enter the event capacity" 
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-400 focus:border-transparent transition-all duration-200"
                  />
                  <p className="text-sm text-gray-600 mt-1">Please specify the number of guests your event can accommodate.</p>
          </div>

                <div className="flex items-center gap-3">
                  <input 
                    className="w-5 h-5 text-orange-400 border-gray-300 rounded focus:ring-orange-400" 
                    checked={eventData.gps_cordinates} 
                    onChange={handleInputChange} 
                    name="gps_cordinates" 
                    type="checkbox" 
                  />
                  <label className="text-sm font-medium text-gray-700">
                    Pin exact event location on map
                  </label>
        </div>
        </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 pt-6 border-t border-gray-200">
              {eventData.id ? (
                <motion.button 
                  type="submit" 
                  className="flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-orange-400 to-orange-500 text-white rounded-lg font-medium hover:from-orange-500 hover:to-orange-600 transition-all duration-200 shadow-lg hover:shadow-xl"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <Edit className="w-4 h-4" />
                  Update Event Information
                </motion.button>
              ) : (
                <motion.button 
                  type="submit" 
                  className="flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-orange-400 to-orange-500 text-white rounded-lg font-medium hover:from-orange-500 hover:to-orange-600 transition-all duration-200 shadow-lg hover:shadow-xl"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <Save className="w-4 h-4" />
                  Save Event Information
                </motion.button>
              )}
              
              <motion.button 
                type="button" 
                onClick={resetEventData} 
                className="flex items-center justify-center gap-2 px-6 py-3 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200 transition-all duration-200"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <Trash2 className="w-4 h-4" />
                Clear Form
              </motion.button>
            </div>
      </form>
    </div>
      </div>
    </motion.div>
  )
}

const TicketInformation = ({ setSelected, setLoading }) => {
  const { getRequest, postRequest, putRequest } = useApiRequest()
  const [categories, setCategories] = useState([])
  const [currencies, setCurrencies] = useState([])
  const [types, setTypes] = useState([])
  const [ticketList, setTicketList] = useState([]) // Store list of tickets
  const [errors, setErrors] = useState({})
  const emptyArray = Array.from({ length: 10 }, (_, index) => index);

  const calculateBookingFee = (price) => {
    if (price <= 50000) return 1000;
    if (price <= 100000) return 1500;
    if (price <= 250000) return 2000;
    if (price <= 500000) return 2500;
    if (price <= 1000000) return 3000;
    return 5000;
  };

  const fetchCategories = async () => {
    const response = await getRequest(`${domain}/ticket-categories`);
    setCategories(response.body);
  };

  const fetchCurrencies = async () => {
    const response = await getRequest(`${domain}/currencies`);
    setCurrencies(response.body);
  };

  const fetchTypes = async () => {
    const response = await getRequest(`${domain}/ticket-types`);
    setTypes(response.body);
  };

  useEffect(() => {
    const fetchData = async () => {
      //setLoading(true);
      try {
        // Perform all fetch requests in parallel and wait for all of them to complete
        await Promise.all([fetchCategories(), fetchCurrencies(), fetchTypes()]);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        // Set loading to false when all requests are completed (or error occurs)
        //setLoading(false);
      }
    };

    fetchData();
  }, []);


  const initialTicketData = {
    ticket_type: "",
    organiser_slug: "",
    event: "",
    eventName: "",
    refund_policy: "",
    ticket_category: "",
    ticketCategoryName: "",
    currency: "",
    ticket_price: "",
    transaction_fees: "",
    who_pays_fees: "",
    ticket_no_people: "",
    sales_start_date: "",
    sales_end_date: "",
    description: "",
    tickets_available: "",
    no_seats_available: "",
    display_count_tickets_available: false,
    allow_guests_book_seat_numbers: false,
    seats_available: "",
    temporary_reservations: "{}"
  }
  const [ticketData, setTicketData] = useState(initialTicketData)
  const [isEditing, setIsEditing] = useState(null); // Track whether editing a ticket
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const [reload, setReload] = useState(false)

  useEffect(() => {
    const eventReg = getFromLocalStorage("eventReg")
    const fetchEventData = async () => {
      const response = await getRequest(`${domain}/event-details?slug=${eventReg}`);
      if (response.status === 200 && response.body.id) {
        setTicketData((prevEventData) => ({
          ...prevEventData,
          event: response.body.id,
          eventName: response.body.event_plot_name,
          organiser_slug: eventReg,
        }));
      } else {
        setTicketData((prevEventData) => ({
          ...prevEventData,
          organiser_slug: eventReg
        }));
      }
    };

    const fetchTicketData = async () => {
      const response = await getRequest(`${domain}/ticket-information?slug=${eventReg}`);
      if (response.status === 200 && response.body.length > 0) {
        const ticketsWithIDs = response.body.map(ticket => ({
          ...ticket,
          id: ticket.id,
          eventName: ticket.event.event_plot_name,
          ticketCategoryName: ticket.ticket_category.name,
          ticket_type: ticket.ticket_type,
          event: ticket.event.id,
          refund_policy: ticket.refund_policy,
          ticket_category: ticket.ticket_category.id,
          currency: ticket.currency,
          ticket_price: ticket.ticket_price,
          transaction_fees: ticket.transaction_fees,
          who_pays_fees: ticket.who_pays_fees,
          ticket_no_people: ticket.ticket_no_people,
          sales_start_date: ticket.sales_start_date,
          sales_end_date: ticket.sales_end_date,
          description: ticket.description,
          tickets_available: ticket.tickets_available,
          no_seats_available: ticket.no_seats_available,
          display_count_tickets_available: ticket.display_count_tickets_available,
          allow_guests_book_seat_numbers: ticket.allow_guests_book_seat_numbers,
          seats_available: ticket.seats_available
          //...response
        }));
        setTicketList(ticketsWithIDs);

        // Update ticketData with dynamic keys from response
        setTicketData(prevTicketData => ({
          ...prevTicketData,
          ...response // Spread all keys from the response
        }));

        console.log(ticketData)

      } else {
        console.error('Error fetching ticket data:', response.error);
      }

    };
    if (eventReg !== null) {
      fetchEventData()
      fetchTicketData()
    }
  }, [reload])

  // Function to handle changes in form inputs
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (name === 'ticket_price') {
      const fee = calculateBookingFee(Number(value));
      setTicketData((prevEventData) => ({
        ...prevEventData,
        transaction_fees: fee
      }));
    }

    if (type === 'checkbox') {
      setTicketData((prevEventData) => ({
        ...prevEventData,
        [name]: checked
      }));
    } else {
      setTicketData((prevEventData) => ({
        ...prevEventData,
        [name]: value
      }));
    }
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  // Validation function
  const validateForm = () => {
    const newErrors = {};
    
    if (!ticketData.ticket_type) {
      newErrors.ticket_type = 'Please select a ticket type';
    }
    
    if (!ticketData.ticket_category) {
      newErrors.ticket_category = 'Please select a ticket category';
    }
    
    if (!ticketData.currency) {
      newErrors.currency = 'Please select a currency';
    }
    
    if (!ticketData.ticket_price || ticketData.ticket_price <= 0) {
      newErrors.ticket_price = 'Please enter a valid ticket price';
    }
    
    if (!ticketData.who_pays_fees) {
      newErrors.who_pays_fees = 'Please select who pays the fees';
    }
    
    if (!ticketData.ticket_no_people) {
      newErrors.ticket_no_people = 'Please select number of people per ticket';
    }
    
    if (!ticketData.sales_start_date) {
      newErrors.sales_start_date = 'Please select sales start date';
    }
    
    if (!ticketData.sales_end_date) {
      newErrors.sales_end_date = 'Please select sales end date';
    }
    
    if (new Date(ticketData.sales_end_date) < new Date(ticketData.sales_start_date)) {
      newErrors.sales_end_date = 'Sales end date must be on or after start date';
    }
    
    if (!ticketData.description.trim()) {
      newErrors.description = 'Please enter a ticket description';
    }
    
    if (!ticketData.tickets_available || ticketData.tickets_available <= 0) {
      newErrors.tickets_available = 'Please enter a valid number of tickets available';
    }
    
    if (ticketData.allow_guests_book_seat_numbers && (!ticketData.no_seats_available || ticketData.no_seats_available <= 0)) {
      newErrors.no_seats_available = 'Please enter a valid number of seats available';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const resetTicketData = () => {
    setTicketData(initialTicketData);
    setIsEditing(null);
    setErrors({});
    const eventReg = getFromLocalStorage("eventReg")
    const fetchEventData = async (slug) => {
      const response = await getRequest(`${domain}/event-details?slug=${eventReg}`);
      if (response.status === 200) {
        setTicketData((prevEventData) => ({
          ...prevEventData,
          event: response.body.id,
          eventName: response.body.event_plot_name,
        }));
      }
    };
    fetchEventData()
  };

  const handleTicketInformation = async (event) => {
    event.preventDefault();

    if (!validateForm()) {
      notify('Please fix the errors in the form', 'error');
      return;
    }

    const formData = new FormData()
    for (const [key, value] of Object.entries(ticketData)) {
      // Handle special cases for foreign key fields
      if (ticketData.id && (key === "ticket_type" || key === "ticket_category" || key === "currency")) {
        // Convert string IDs to integers for backend foreign key fields
        formData.append(key, parseInt(value));
      } else if (key === "no_seats_available") {
        // Handle seats field based on checkbox state
        if (ticketData.allow_guests_book_seat_numbers && value && value > 0) {
          formData.append(key, parseInt(value));
        } else {
          // Send 0 when checkbox is unchecked (seats are optional)
          formData.append(key, 0);
        }
      } else if (key === "temporary_reservations") {
        // Ensure temporary_reservations is always valid JSON
        try {
          if (typeof value === 'string') {
            // If it's already a string, validate it's JSON
            JSON.parse(value);
            formData.append(key, value);
          } else if (typeof value === 'object') {
            // If it's an object, stringify it
            formData.append(key, JSON.stringify(value));
          } else {
            // Default to empty object
            formData.append(key, "{}");
          }
        } catch (error) {
          // If JSON parsing fails, send empty object
          console.warn('Invalid temporary_reservations JSON, sending empty object:', error);
          formData.append(key, "{}");
        }
      } else {
        formData.append(key, value);
      }
    }

    setLoading(true)

    let response;

    if (isEditing !== null) {
      // Update the existing ticket
      response = await putRequest(`${domain}/ticket-information/${ticketList[isEditing].id}`, formData, true);
      if (response.status === 202) {
        setTicketList((prevList) =>
          prevList.map((ticket, index) =>
            index === isEditing ? ticketData : ticket
          )
        );
      }
    } else {
      // Add a new ticket
      response = await postRequest(`${domain}/ticket-information`, formData, true);
      if (response.status === 201) {
        // Add the new ticket with ID to the ticketList
        const newTicket = { ...ticketData, id: response.body.data.id }; // Assuming the response contains the new ticket ID
        setTicketList([...ticketList, newTicket]);
        //setTicketList([...ticketList, ticketData]);
      }
    }

    if (response.status === 201 || response.status === 202) {
      notify(isEditing !== null ? 'Ticket updated successfully!' : 'Ticket created successfully!', "success")
      if (isEditing === null) {
        resetTicketData();
      }
    } else {
      notify(response.body || 'Failed to save ticket', "error")
    }
    setLoading(false)

    //event.target.reset()
    //resetTicketData()
    setReload(!reload)
  };

  const handleEditTicket = (index) => {
    // Load the selected ticket data into the form
    const ticketToEdit = ticketList[index];
    setTicketData({
      ...ticketToEdit,
      // Ensure ticket_type is set correctly for the select field
      ticket_type: ticketToEdit.ticket_type?.id || ticketToEdit.ticket_type,
      ticket_category: ticketToEdit.ticket_category?.id || ticketToEdit.ticket_category,
      currency: ticketToEdit.currency?.id || ticketToEdit.currency,
      // Ensure temporary_reservations is valid JSON
      temporary_reservations: ticketToEdit.temporary_reservations || "{}"
    });
    setIsEditing(index);
  };

  return (
    <motion.div 
      className='min-h-screen bg-gray-50 py-8'
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <div className="w-full md:w-[70%] mx-auto px-4 py-6">
        <div className="bg-white rounded-xl shadow-lg p-8">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 bg-gradient-to-br from-orange-400 to-orange-500 rounded-xl flex items-center justify-center">
              <Ticket className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-800">Ticket Information</h2>
              <p className="text-gray-600">Configure your event tickets</p>
            </div>
          </div>

          {/* Added Tickets Section */}
          {ticketList.length > 0 && (
            <div className="mb-8 p-6 bg-gray-50 rounded-lg border border-gray-200">
              <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                <Ticket className="w-5 h-5 text-orange-400" />
                Added Tickets ({ticketList.length})
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {ticketList.map((ticket, index) => (
                  <motion.div
            key={index}
                    className={`p-4 rounded-lg border-2 cursor-pointer transition-all duration-200 ${
                      hoveredIndex === index 
                        ? 'border-orange-400 bg-orange-50 shadow-md' 
                        : 'border-gray-200 bg-white hover:border-gray-300'
                    }`}
            onClick={() => handleEditTicket(index)}
            onMouseEnter={() => setHoveredIndex(index)}
            onMouseLeave={() => setHoveredIndex(null)}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium text-gray-800">{ticket.ticketCategoryName}</span>
                      <span className="text-sm text-gray-500">#{index + 1}</span>
                    </div>
                    <div className="text-lg font-bold text-orange-500">
                      {ticket.currency} {ticket.ticket_price}
                    </div>
                    <div className="text-sm text-gray-600 mt-1">
                      {ticket.tickets_available} tickets available
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          )}

          {/* Continue to Preview Button - More Prominent Position */}
          {ticketList.length > 0 && (
            <div className="mb-8 p-6 bg-gradient-to-r from-teal-50 to-blue-50 rounded-xl border-2 border-teal-200">
              <div className="text-center">
                <h3 className="text-lg font-semibold text-gray-800 mb-2 flex items-center justify-center gap-2">
                  <CheckCircle className="w-5 h-5 text-teal-500" />
                  Ready to Preview Your Event
                </h3>
                <p className="text-gray-600 mb-4">You have successfully added {ticketList.length} ticket{ticketList.length > 1 ? 's' : ''}. You can now preview your event or continue adding more tickets.</p>
                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                  <motion.button
            type="button"
                    className="flex items-center justify-center gap-2 px-8 py-4 bg-gradient-to-r from-teal-500 to-teal-600 text-white rounded-xl font-semibold hover:from-teal-600 hover:to-teal-700 transition-all duration-200 shadow-lg hover:shadow-xl"
            onClick={() => setSelected(3)}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <ArrowRight className="w-5 h-5" />
                    Continue to Preview
                  </motion.button>
                  <motion.button
                    type="button"
                    className="flex items-center justify-center gap-2 px-6 py-4 bg-white text-gray-700 rounded-xl font-medium border-2 border-gray-200 hover:border-gray-300 transition-all duration-200 shadow-sm hover:shadow-md"
                    onClick={() => {
                      resetTicketData();
                      setIsEditing(null);
                    }}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <Plus className="w-4 h-4" />
                    Add Another Ticket
                  </motion.button>
                </div>
              </div>
            </div>
          )}

          <form onSubmit={handleTicketInformation} className="space-y-8">
            {/* Basic Ticket Information */}
            <div className="space-y-6">
              <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                <Ticket className="w-5 h-5 text-orange-400" />
                Basic Information
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Ticket Type *
                  </label>
                  <select 
                    name="ticket_type" 
                    value={ticketData.ticket_type} 
                    onChange={handleInputChange} 
                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-orange-400 focus:border-transparent transition-all duration-200 ${
                      errors.ticket_type ? 'border-red-500' : 'border-gray-300'
                    }`}
                  >
                    <option value="">Select a ticket type</option>
              {types.length > 0 && types.map(item =>
                <option key={item.id} value={item.id}>{item.name}</option>
              )}
            </select>
                  {errors.ticket_type && (
                    <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
                      <AlertCircle className="w-4 h-4" />
                      {errors.ticket_type}
                    </p>
                  )}
                  <p className="text-sm text-gray-600 mt-1">Select the type of ticket this is.</p>
          </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Event Attached
                  </label>
                  <input 
                    type="text" 
                    name="eventName" 
                    readOnly 
                    value={ticketData.eventName} 
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-gray-50 text-gray-600"
                    placeholder="Event plot name"
                  />
                  <p className="text-sm text-gray-600 mt-1">Event that this ticket is attached to.</p>
            </div>
          </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Ticket Category *
                </label>
                <select 
                  name="ticket_category" 
                  value={ticketData.ticket_category} 
                  onChange={handleInputChange} 
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-orange-400 focus:border-transparent transition-all duration-200 ${
                    errors.ticket_category ? 'border-red-500' : 'border-gray-300'
                  }`}
                >
                  <option value="">Select a ticket category</option>
              {categories.length > 0 && categories.map(item =>
                <option key={item.id} value={item.id}>{item.name}</option>
              )}
            </select>
                {errors.ticket_category && (
                  <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
                    <AlertCircle className="w-4 h-4" />
                    {errors.ticket_category}
                  </p>
                )}
                <p className="text-sm text-gray-600 mt-1">Select the Category of this ticket.</p>
          </div>
        </div>

            {/* Pricing Information */}
            <div className="space-y-6">
              <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                <DollarSign className="w-5 h-5 text-orange-400" />
                Pricing Information
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Currency *
                  </label>
                  <select 
                    name="currency" 
                    value={ticketData.currency} 
                    onChange={handleInputChange} 
                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-orange-400 focus:border-transparent transition-all duration-200 ${
                      errors.currency ? 'border-red-500' : 'border-gray-300'
                    }`}
                  >
                    <option value="">Select a currency</option>
              {currencies.length > 0 && currencies.map(item =>
                <option key={item.id} value={item.id}>{item.name}</option>
              )}
            </select>
                  {errors.currency && (
                    <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
                      <AlertCircle className="w-4 h-4" />
                      {errors.currency}
                    </p>
                  )}
                  <p className="text-sm text-gray-600 mt-1">Select the Currency to use.</p>
          </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Ticket Price *
                  </label>
                  <input 
                    type="number" 
                    name="ticket_price" 
                    value={ticketData.ticket_price} 
                    onChange={handleInputChange} 
                    placeholder="Enter ticket price" 
                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-orange-400 focus:border-transparent transition-all duration-200 ${
                      errors.ticket_price ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  {errors.ticket_price && (
                    <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
                      <AlertCircle className="w-4 h-4" />
                      {errors.ticket_price}
                    </p>
                  )}
                  <p className="text-sm text-gray-600 mt-1">Amount of the ticket in selected currency.</p>
            </div>
          </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Booking Fee
                </label>
            <input
              type="number"
              value={ticketData.transaction_fees}
              readOnly
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-gray-50 text-gray-600"
            />
                <p className="text-sm text-gray-600 mt-1">Calculated based on ticket price.</p>
          </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Who Pays Fees *
                </label>
                <select 
                  name="who_pays_fees" 
                  value={ticketData.who_pays_fees} 
                  onChange={handleInputChange} 
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-orange-400 focus:border-transparent transition-all duration-200 ${
                    errors.who_pays_fees ? 'border-red-500' : 'border-gray-300'
                  }`}
                >
                  <option value="">Select who pays</option>
              <option value='USER'>User</option>
              <option value='ORGANISER'>Organiser</option>
            </select>
                {errors.who_pays_fees && (
                  <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
                    <AlertCircle className="w-4 h-4" />
                    {errors.who_pays_fees}
                  </p>
                )}
                <p className="text-sm text-gray-600 mt-1">Who pays Transaction fees.</p>
          </div>
            </div>

            {/* Ticket Details */}
            <div className="space-y-6">
              <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                <Users className="w-5 h-5 text-orange-400" />
                Ticket Details
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    People per Ticket *
                  </label>
                  <select 
                    name="ticket_no_people" 
                    value={ticketData.ticket_no_people} 
                    onChange={handleInputChange} 
                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-orange-400 focus:border-transparent transition-all duration-200 ${
                      errors.ticket_no_people ? 'border-red-500' : 'border-gray-300'
                    }`}
                  >
                    <option value="">Select the number of people</option>
              {emptyArray.map(item =>
                <option key={item} value={item + 1}>{item + 1}</option>
              )}
            </select>
                  {errors.ticket_no_people && (
                    <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
                      <AlertCircle className="w-4 h-4" />
                      {errors.ticket_no_people}
                    </p>
                  )}
                  <p className="text-sm text-gray-600 mt-1">Number of people Ticket Permits</p>
          </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Tickets Available *
                  </label>
                  <input 
                    type="number" 
                    name="tickets_available" 
                    value={ticketData.tickets_available} 
                    onChange={handleInputChange} 
                    placeholder="Enter number of tickets" 
                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-orange-400 focus:border-transparent transition-all duration-200 ${
                      errors.tickets_available ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  {errors.tickets_available && (
                    <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
                      <AlertCircle className="w-4 h-4" />
                      {errors.tickets_available}
                    </p>
                  )}
                  <p className="text-sm text-gray-600 mt-1">Provide the total number of tickets available.</p>
              </div>
              </div>

              <div className="flex items-center gap-3">
                <input 
                  className="w-5 h-5 text-orange-400 border-gray-300 rounded focus:ring-orange-400" 
                  type="checkbox" 
                  name="display_count_tickets_available" 
                  checked={ticketData.display_count_tickets_available} 
                  onChange={handleInputChange} 
                />
                <label className="text-sm font-medium text-gray-700">
                  Display number of tickets available
                </label>
            </div>
          </div>

            {/* Sales Schedule */}
            <div className="space-y-6">
              <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                <Calendar className="w-5 h-5 text-orange-400" />
                Sales Schedule
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Sales Start Date *
                  </label>
                  <input 
                    type="date" 
                    name="sales_start_date" 
                    value={ticketData.sales_start_date} 
                    onChange={handleInputChange} 
                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-orange-400 focus:border-transparent transition-all duration-200 ${
                      errors.sales_start_date ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  {errors.sales_start_date && (
                    <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
                      <AlertCircle className="w-4 h-4" />
                      {errors.sales_start_date}
                    </p>
                  )}
                  <p className="text-sm text-gray-600 mt-1">Sales start date</p>
        </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Sales End Date *
                  </label>
                  <input 
                    type="date" 
                    name="sales_end_date" 
                    value={ticketData.sales_end_date} 
                    onChange={handleInputChange} 
                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-orange-400 focus:border-transparent transition-all duration-200 ${
                      errors.sales_end_date ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  {errors.sales_end_date && (
                    <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
                      <AlertCircle className="w-4 h-4" />
                      {errors.sales_end_date}
                    </p>
                  )}
                  <p className="text-sm text-gray-600 mt-1">Sales end date</p>
        </div>
            </div>
          </div>

            {/* Ticket Description */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                <Settings className="w-5 h-5 text-orange-400" />
                Additional Information
              </h3>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Ticket Description *
                </label>
                <textarea 
                  name="description" 
                  value={ticketData.description} 
                  onChange={handleInputChange} 
                  rows="4" 
                  placeholder="Enter the ticket description here" 
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-orange-400 focus:border-transparent transition-all duration-200 ${
                    errors.description ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {errors.description && (
                  <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
                    <AlertCircle className="w-4 h-4" />
                    {errors.description}
                  </p>
                )}
                <p className="text-sm text-gray-600 mt-1">Provide all the description available to the ticket.</p>
            </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Refund Policy
                </label>
                <textarea 
                  name="refund_policy" 
                  value={ticketData.refund_policy} 
                  onChange={handleInputChange} 
                  rows="3" 
                  placeholder="Provide all the available refund policy to the ticket." 
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-400 focus:border-transparent transition-all duration-200"
                />
                <p className="text-sm text-gray-600 mt-1">Provide all the available refund policy to the ticket.</p>
          </div>

              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <input 
                    className="w-5 h-5 text-orange-400 border-gray-300 rounded focus:ring-orange-400" 
                    type="checkbox" 
                    name="allow_guests_book_seat_numbers" 
                    checked={ticketData.allow_guests_book_seat_numbers} 
                    onChange={handleInputChange} 
                  />
                  <label className="text-sm font-medium text-gray-700">
                    Allow guests to book seat numbers
                  </label>
            </div>
                <p className="text-sm text-gray-600">Activating this would enable guests to book seat numbers and you should provide the range of seats available for booking.</p>
                
                <AnimatePresence>
                  {ticketData.allow_guests_book_seat_numbers && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.3 }}
                      className="space-y-4 p-4 bg-gray-50 rounded-lg border border-gray-200"
                    >
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Seats Available *
                      </label>
                      <input 
                        type="number" 
                        name="no_seats_available" 
                        value={ticketData.no_seats_available} 
                        onChange={handleInputChange} 
                        placeholder="How many seats are available" 
                        className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-orange-400 focus:border-transparent transition-all duration-200 ${
                          errors.no_seats_available ? 'border-red-500' : 'border-gray-300'
                        }`}
                      />
                      {errors.no_seats_available && (
                        <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
                          <AlertCircle className="w-4 h-4" />
                          {errors.no_seats_available}
                        </p>
                      )}
                      <p className="text-sm text-gray-600">Provide the number of seats available</p>
                    </motion.div>
                  )}
                </AnimatePresence>
        </div>
        </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 pt-6 border-t border-gray-200">
              {isEditing !== null ? (
                <motion.button 
                  type="submit" 
                  className="flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-orange-400 to-orange-500 text-white rounded-lg font-medium hover:from-orange-500 hover:to-orange-600 transition-all duration-200 shadow-lg hover:shadow-xl"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <Edit className="w-4 h-4" />
                  Update Ticket
                </motion.button>
              ) : (
                <motion.button 
                  type="submit" 
                  className="flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-orange-400 to-orange-500 text-white rounded-lg font-medium hover:from-orange-500 hover:to-orange-600 transition-all duration-200 shadow-lg hover:shadow-xl"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <Save className="w-4 h-4" />
                  Create Ticket
                </motion.button>
              )}

        {isEditing !== null && (
                <motion.button
            type="button"
                  className="flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-teal-400 to-teal-500 text-white rounded-lg font-medium hover:from-teal-500 hover:to-teal-600 transition-all duration-200 shadow-lg hover:shadow-xl"
                  onClick={resetTicketData}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <Plus className="w-4 h-4" />
                  Add New Ticket
                </motion.button>
              )}
              
              <motion.button 
            type="button"
                onClick={resetTicketData} 
                className="flex items-center justify-center gap-2 px-6 py-3 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200 transition-all duration-200"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <Trash2 className="w-4 h-4" />
                Clear Form
              </motion.button>
            </div>
      </form>
    </div>
      </div>
    </motion.div>
  )
}

const EventPreviews = ({ setReload, reload }) => {
  const { postRequest } = useApiRequest()
  const navigate = useNavigate()
  const [regData, setRegData] = useState("")
  const [completed, setCompleted] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  
  useEffect(() => {
    const eventReg = getFromLocalStorage("eventReg")
    setRegData(eventReg)
  }, [])

  const handleApprove = async () => {
    setIsSubmitting(true)
    const formData = new FormData()
    formData.append("organiser_slug", regData)
    
    try {
    const req = await postRequest(`${domain}/approve-event`, formData, true)
    if (req.body.status === 'ok' && req.status === 200) {
      setCompleted(true)
        notify('Event approved successfully!', 'success')
      if (localStorage.getItem("eventReg")) {
        localStorage.removeItem("eventReg")
      }
      if (localStorage.getItem("edit")) {
        localStorage.removeItem("edit");
      }
      setReload(!reload)
      navigate(`/`);
    } else {
        notify(req.body.error || 'Failed to approve event', 'error')
      }
    } catch (error) {
      notify('An error occurred while approving the event', 'error')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <motion.div 
      className='min-h-screen bg-gray-50 py-8'
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <div className="container mx-auto px-4 max-w-6xl">
        <div className="bg-white rounded-xl shadow-lg p-8">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 bg-gradient-to-br from-orange-400 to-orange-500 rounded-xl flex items-center justify-center">
              <Eye className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-800">Event Preview</h2>
              <p className="text-gray-600">Review and approve your event</p>
            </div>
          </div>

          <div className="space-y-6">
            <div className="p-6 bg-blue-50 rounded-lg border border-blue-200">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center">
                  <CheckCircle className="w-5 h-5 text-white" />
                </div>
                <h3 className="text-lg font-semibold text-blue-800">Almost Done!</h3>
              </div>
              <p className="text-blue-700">
                You've successfully created your event. Please review all the information below and approve when ready.
                Once approved, your event will be live on EventPlots!
              </p>
            </div>

            <EventPreview completed={completed} regData={regData} handleApprove={handleApprove} />

            {!completed && (
              <div className="flex flex-col sm:flex-row gap-4 pt-6 border-t border-gray-200">
                <motion.button
                  onClick={handleApprove}
                  disabled={isSubmitting}
                  className="flex items-center justify-center gap-2 px-8 py-4 bg-gradient-to-r from-green-400 to-green-500 text-white rounded-lg font-medium hover:from-green-500 hover:to-green-600 transition-all duration-200 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
                  whileHover={{ scale: isSubmitting ? 1 : 1.02 }}
                  whileTap={{ scale: isSubmitting ? 1 : 0.98 }}
                >
                  {isSubmitting ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      Approving...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-5 h-5" />
                      Approve & Publish Event
                    </>
                  )}
                </motion.button>
                
                <motion.button
                  onClick={() => navigate('/my-events')}
                  className="flex items-center justify-center gap-2 px-8 py-4 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200 transition-all duration-200"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <ArrowLeft className="w-5 h-5" />
                  Back to My Events
                </motion.button>
              </div>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  )
}


const CreatePlot = () => {
  const [reload, setReload] = useState(false)
  const { previousUrl, currentUrl } = useUrlContext();
  const { event, setEvent } = useEventContext()
  const [selected, setSelected] = useState(0)
  const { setLoading } = useLoadingContext()
  const { isLoggedIn, userInfo } = useLoginContext()
  const [modalVisible, setModalVisible] = useState(false);

  useEffect(() => {
    try {
      const editing = localStorage.getItem("edit");

      // Ensure `editing` is parsed safely and compared properly
      if (editing && JSON.parse(editing) === true && previousUrl !== "/my-events") {
        setModalVisible(true); // Show the modal
      } else {
        setModalVisible(false); // Hide the modal
      }
    } catch (error) {
      console.error("Error accessing localStorage:", error);
    }
  }, [previousUrl]);

  const handleContinueEditing = () => {
    setModalVisible(false);
    // Continue with editing, any necessary state updates
    console.log("Continuing with editing...");
  };

  const handleCreateNew = () => {
    setModalVisible(false);
    // Clear the existing edit from localStorage
    localStorage.removeItem("edit");
    if (localStorage.getItem("eventReg")) {
      localStorage.removeItem("eventReg")
    }
    setReload(!reload)
    console.log("Creating a new item...");
  };

  const handleCloseModal = () => {
    setModalVisible(false); // Close the modal without making any changes
  };

  return (
    <div key={reload} className='animate__animated animate__fadeIn'>
      <Header />
      <p className='d-none'>Previous URL: {previousUrl}</p>
      <p className='d-none'>Current URL: {currentUrl}</p>
      <Modal show={modalVisible} onHide={handleCloseModal}>
        <Modal.Header closeButton>
          <Modal.Title>Do you want to continue editing or create a new item?</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>You already have an item in progress. Would you like to continue editing or start a new one?</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseModal}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleContinueEditing}>
            Continue Editing
          </Button>
          <Button variant="danger" onClick={handleCreateNew}>
            Create New
          </Button>
        </Modal.Footer>
      </Modal>
      {userInfo.accessToken ?
        <>
          <Nav selected={selected} setSelected={setSelected} />
          {selected === 0 && <OrganiserProfile setSelected={setSelected} setLoading={setLoading} event={event} setEvent={setEvent} />}
          {selected === 1 && <EventDetail setSelected={setSelected} setLoading={setLoading} event={event} setEvent={setEvent} />}
          {selected === 2 && <TicketInformation setSelected={setSelected} setLoading={setLoading} event={event} setEvent={setEvent} />}
          {/*selected === 3 && <AccountDetails setSelected={setSelected} setLoading={setLoading} event={event} setEvent={setEvent} />*/}
          {selected === 3 && <EventPreviews setReload={setReload} reload={reload} />}
        </>
        :
        <motion.div 
          className='min-h-screen bg-gray-50 py-8'
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="container mx-auto px-4 max-w-4xl">
            <div className="bg-white rounded-xl shadow-lg p-8 text-center">
              <div className="w-24 h-24 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <AlertCircle className="w-12 h-12 text-red-500" />
              </div>
              
              <h1 className="text-3xl font-bold text-gray-800 mb-4">Access Restricted</h1>
              <p className="text-lg text-gray-600 mb-8">
                You need to be logged in to create an event or register with EventPlots.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <motion.button
                  onClick={() => navigate('/login')}
                  className="flex items-center justify-center gap-2 px-8 py-4 bg-gradient-to-r from-orange-400 to-orange-500 text-white rounded-lg font-medium hover:from-orange-500 hover:to-orange-600 transition-all duration-200 shadow-lg hover:shadow-xl"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <User className="w-5 h-5" />
                  Log In
                </motion.button>
                
                <motion.button
                  onClick={() => navigate('/register')}
                  className="flex items-center justify-center gap-2 px-8 py-4 bg-gradient-to-r from-teal-400 to-teal-500 text-white rounded-lg font-medium hover:from-teal-500 hover:to-teal-600 transition-all duration-200 shadow-lg hover:shadow-xl"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <Plus className="w-5 h-5" />
                  Register
                </motion.button>
            </div>
              
              <div className="mt-8 p-6 bg-gray-50 rounded-lg">
                <h3 className="text-lg font-semibold text-gray-800 mb-3">Why create an account?</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-gray-600">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    <span>Create and manage events</span>
          </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    <span>Track ticket sales</span>
        </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    <span>Access analytics</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      }
    </div>
  )
}

export default CreatePlot